import { useState, useCallback } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  pulseCheckCreatePermission,
  type CheckDefinition,
} from '@internal/backstage-plugin-pulse-common';
import {
  invalidateCache,
  PULSE_CACHE_KEYS,
  PULSE_CACHE_KEY_PREFIXES,
} from './cache';

const errorFromResponse = async (
  response: Response,
  fallback: string,
): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
};

/** Creates a new UI-managed Pulse check definition. */
export const useCreateCheck = () => {
  const { fetch } = useApi(fetchApiRef);
  const { allowed: canCreate } = usePermission({
    permission: pulseCheckCreatePermission,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(
    async (check: CheckDefinition): Promise<CheckDefinition> => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('plugin://pulse/checks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(check),
        });
        if (!response.ok) {
          throw await errorFromResponse(
            response,
            `Failed to create check: ${response.status}`,
          );
        }
        const created = await response.json();
        invalidateCache(PULSE_CACHE_KEYS.checks);
        invalidateCache(PULSE_CACHE_KEY_PREFIXES.trackChecks);
        invalidateCache(PULSE_CACHE_KEY_PREFIXES.insights);
        invalidateCache(PULSE_CACHE_KEY_PREFIXES.integrationHealth);
        return created;
      } catch (error_) {
        const err = error_ as Error;
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetch],
  );

  return { canCreate, create, loading, error };
};
